import { TASK_PRIORITY } from "./taskPriority";

// Extraido do CommandPalette para ser compartilhado entre o palette do desktop
// e a captura de lembretes no celular.

export function toYMD(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseDateToken(token) {
  const t = (token || "").toLowerCase();

  if (t === "hoje") return toYMD(new Date());
  if (t === "amanha" || t === "amanhã") {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return toYMD(d);
  }

  // dd/mm/yyyy
  const m = token.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) {
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm}-${dd}`;
  }

  // yyyy-mm-dd
  const m2 = token.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m2) return token;

  return null;
}

// Escrito com escapes ASCII de proposito: um literal de acentos combinantes
// aqui sobrevive mal a mudancas de encoding do arquivo.
const ACENTOS_COMBINANTES = new RegExp("[\\u0300-\\u036f]", "g");

export function normalizePriorityToken(p) {
  const x = (p || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(ACENTOS_COMBINANTES, "")
    .trim();

  if (x === "1" || ["urgente", "urgent", "critica", "critico"].includes(x)) return TASK_PRIORITY.URGENT;
  if (x === "2" || ["alta", "high"].includes(x)) return TASK_PRIORITY.HIGH;
  if (x === "3" || ["media", "medium"].includes(x)) return TASK_PRIORITY.MEDIUM;
  if (x === "4" || ["baixa", "low"].includes(x)) return TASK_PRIORITY.LOW;
  return TASK_PRIORITY.MEDIUM;
}

/**
 * Tokens suportados:
 *   #projeto  @pessoa  !prioridade
 *   hoje | amanha | dd/mm/aaaa       -> vencimento
 *   ^hoje | ^dd/mm/aaaa              -> data de inicio
 */
export function parseQuickTask(input) {
  const raw = (input || "").trim();
  const tokens = raw.split(/\s+/).filter(Boolean);

  let projetoSlug = null;
  let responsavelSlug = null;
  let prioridade = TASK_PRIORITY.MEDIUM;
  let vencimento = null;
  let dataInicio = null;

  const descParts = [];

  for (const t of tokens) {
    if (t.startsWith("#") && t.length > 1) {
      projetoSlug = t.substring(1);
      continue;
    }

    if (t.startsWith("@") && t.length > 1) {
      responsavelSlug = t.substring(1);
      continue;
    }

    if (t.startsWith("!") && t.length > 1) {
      prioridade = normalizePriorityToken(t.substring(1));
      continue;
    }

    if (t.startsWith("^") && t.length > 1) {
      const parsed = parseDateToken(t.substring(1));
      if (parsed) dataInicio = parsed;
      else descParts.push(t);
      continue;
    }

    const parsedDate = parseDateToken(t);
    if (parsedDate) {
      vencimento = parsedDate;
      continue;
    }

    descParts.push(t);
  }

  return {
    descricao: descParts.join(" ").trim(),
    projetoSlug,
    responsavelSlug,
    prioridade,
    vencimento,
    dataInicio,
  };
}

function normalizeText(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(ACENTOS_COMBINANTES, "")
    .trim();
}

const nomeDoMembro = (m) => m?.nome || m?.descricao || m?.email || "";

/** Slug sem espaco/acento a partir do primeiro nome - o que o @ do palette insere. */
export function memberSlug(member) {
  return normalizeText(nomeDoMembro(member)).split(/\s+/)[0] || "";
}

/**
 * Resolve o texto depois do @ contra a lista de membros do workspace.
 * "me"/"eu"/"mim" sempre volta pro proprio usuario. Sem slug nenhum, tambem
 * (continua sendo o default seguro). Varios membros batendo o mesmo slug
 * (ex: dois "joao") marca ambiguous=true em vez de chutar um - melhor a
 * pessoa ver isso no preview do que a tarefa cair com o responsavel errado.
 */
export function resolveResponsavel(slug, membros, currentUserId) {
  if (!slug) return { id: currentUserId || null, membro: null, ambiguo: false, naoEncontrado: false };

  const alvo = normalizeText(slug);
  if (["me", "eu", "mim"].includes(alvo)) {
    return { id: currentUserId || null, membro: null, ambiguo: false, naoEncontrado: false };
  }

  const lista = Array.isArray(membros) ? membros : [];
  const candidatos = lista.filter((m) => {
    const nome = normalizeText(nomeDoMembro(m));
    const email = normalizeText(m?.email);
    return (
      nome === alvo ||
      email.startsWith(alvo) ||
      nome.split(/\s+/).some((palavra) => palavra === alvo || palavra.startsWith(alvo))
    );
  });

  if (candidatos.length === 1) {
    const m = candidatos[0];
    return { id: m.id_usuario ?? m.id, membro: m, ambiguo: false, naoEncontrado: false };
  }
  if (candidatos.length > 1) {
    return { id: null, membro: null, ambiguo: true, naoEncontrado: false, candidatos };
  }
  return { id: null, membro: null, ambiguo: false, naoEncontrado: true };
}
